import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { FiShield, FiSearch, FiFilter, FiRefreshCw, FiLogIn, FiLogOut, FiEdit, FiTrash2, FiPlus, FiSend, FiUser, FiDatabase, FiCalendar } from 'react-icons/fi';

/* ══ CONQUER Cyan Theme ══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  dark: '#0A1628',
  slate: '#0096C7',
  muted: '#48CAE4',
};

const ACTION_META = {
  LOGIN: { icon: FiLogIn, color: THEME.primary, label: 'Login' },
  LOGOUT: { icon: FiLogOut, color: '#64748b', label: 'Logout' },
  LOGIN_FAILED: { icon: FiLogIn, color: THEME.danger, label: 'Failed Login' },
  CREATE_USER: { icon: FiPlus, color: THEME.success, label: 'User Created' },
  UPDATE_USER: { icon: FiEdit, color: THEME.warning, label: 'User Updated' },
  DELETE_USER: { icon: FiTrash2, color: THEME.danger, label: 'User Deleted' },
  CREATE_CAMPAIGN: { icon: FiPlus, color: THEME.slate, label: 'Campaign Created' },
  UPDATE_CAMPAIGN: { icon: FiEdit, color: THEME.warning, label: 'Campaign Updated' },
  DELETE_CAMPAIGN: { icon: FiTrash2, color: THEME.danger, label: 'Campaign Deleted' },
  SEND_MESSAGE: { icon: FiSend, color: THEME.success, label: 'Message Sent' },
  UPLOAD_DATA: { icon: FiDatabase, color: '#06b6d4', label: 'Data Upload' },
  SCHEDULE: { icon: FiCalendar, color: THEME.secondary, label: 'Scheduled' },
};

const getActionMeta = (action) => {
  const key = Object.keys(ACTION_META).find(k => action?.toUpperCase().includes(k));
  return ACTION_META[key] || { icon: FiShield, color: '#64748b', label: action };
};

const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div style={{
    background: '#fff', borderRadius: 14, padding: 18,
    border: `1px solid ${THEME.accent}30`, boxShadow: '0 2px 12px rgba(124,58,237,0.06)',
    display: 'flex', alignItems: 'center', gap: 14,
  }}>
    <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={22} style={{ color }} />
    </div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: THEME.dark }}>{value}</div>
      <div style={{ fontSize: 12, color: THEME.muted, fontWeight: 600 }}>{label}</div>
    </div>
  </div>
);

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (search) params.search = search;
      if (actionFilter) params.action = actionFilter;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const { data } = await api.get('/admin/audit-logs', { params });
      setLogs(data.logs || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [page, actionFilter]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchLogs(); };

  const todayLogs = logs.filter(l => new Date(l.createdAt).toDateString() === new Date().toDateString()).length;
  const loginLogs = logs.filter(l => l.action?.includes('LOGIN') && !l.action?.includes('FAILED')).length;
  const failedLogs = logs.filter(l => l.action?.includes('FAILED')).length;

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid #E0E7FF',
    fontSize: 13, color: THEME.dark, background: '#FAFAFF', outline: 'none',
  };

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: THEME.dark, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiShield size={28} style={{ color: THEME.primary }} />
            Activity Trail
          </h1>
          <p style={{ fontSize: 14, color: THEME.muted, marginTop: 6 }}>Complete audit log — who did what, when</p>
        </div>
        <button onClick={() => { setPage(1); fetchLogs(); }}
          style={{ padding: '12px 20px', borderRadius: 12, border: '1px solid #E0E7FF', background: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: THEME.slate }}>
          <FiRefreshCw size={18} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard icon={FiShield} label="Total Events" value={total.toLocaleString()} color={THEME.primary} />
        <StatCard icon={FiCalendar} label="Today's Events" value={todayLogs} color={THEME.slate} />
        <StatCard icon={FiLogIn} label="Logins (this page)" value={loginLogs} color={THEME.success} />
        <StatCard icon={FiShield} label="Failed Actions" value={failedLogs} color={THEME.danger} />
      </div>

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: 16, padding: 20, marginBottom: 24,
        border: `1px solid ${THEME.accent}30`, boxShadow: '0 2px 12px rgba(124,58,237,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: `${THEME.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FiFilter size={16} style={{ color: THEME.primary }} />
          </div>
          <span style={{ fontSize: 15, fontWeight: 700, color: THEME.dark }}>Filters</span>
        </div>
        <form onSubmit={handleSearch} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Search</label>
            <div style={{ position: 'relative' }}>
              <FiSearch size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: THEME.muted }} />
              <input type="text" placeholder="Username, action, details..." value={search}
                onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, paddingLeft: 36 }} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Action Type</label>
            <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">All Actions</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="SEND">Send</option>
              <option value="UPLOAD">Upload</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>From</label>
            <input type="date" value={dateRange.start} onChange={e => setDateRange({ ...dateRange, start: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>To</label>
            <input type="date" value={dateRange.end} onChange={e => setDateRange({ ...dateRange, end: e.target.value })} style={inputStyle} />
          </div>
          <button type="submit" style={{
            width: 44, height: 44, borderRadius: 10, border: 'none',
            background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FiSearch size={18} style={{ color: '#fff' }} />
          </button>
        </form>
      </div>

      {/* Logs Timeline */}
      <div style={{
        background: '#fff', borderRadius: 18, padding: 20,
        border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)',
      }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, border: '4px solid #E0E7FF', borderTopColor: THEME.primary, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: THEME.muted }}>
            <FiShield size={48} style={{ marginBottom: 16, opacity: 0.4 }} />
            <div style={{ fontSize: 16, fontWeight: 600 }}>No audit logs found</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Adjust your filters or check back later</div>
          </div>
        ) : (
          <>
            {logs.map((log, i) => {
              const meta = getActionMeta(log.action);
              const Icon = meta.icon;
              return (
                <div key={log._id || i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0',
                  borderBottom: i < logs.length - 1 ? '1px solid #F0EEFF' : 'none',
                }}>
                  <div style={{
                    width: 40, height: 40, minWidth: 40, borderRadius: 10,
                    background: `${meta.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={18} style={{ color: meta.color }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: THEME.dark }}>{log.username || 'System'}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 8,
                        background: `${meta.color}15`, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>{meta.label}</span>
                    </div>
                    {log.details && <p style={{ fontSize: 13, color: '#64748b', margin: 0, lineHeight: 1.5 }}>{log.details}</p>}
                    {log.ipAddress && <span style={{ fontSize: 11, color: THEME.muted }}>IP: {log.ipAddress}</span>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: THEME.slate }}>{timeAgo(log.createdAt)}</div>
                    <div style={{ fontSize: 11, color: THEME.muted }}>{new Date(log.createdAt).toLocaleTimeString()}</div>
                  </div>
                </div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, paddingTop: 20, marginTop: 16, borderTop: '1px solid #F0EEFF' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: THEME.slate }}>‹</button>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => (
                  <button key={i} onClick={() => setPage(i + 1)}
                    style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: page === i + 1 ? THEME.primary : '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: page === i + 1 ? '#fff' : THEME.slate }}>{i + 1}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: THEME.slate }}>›</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
